import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Star, ChevronRight, CheckCircle2, Award, Menu, X } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import BeforeAfterSlider from "@/components/site/BeforeAfterSlider";
import ReviewText from "@/components/site/ReviewText";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BITTER = { fontFamily: "'Bitter', Georgia, serif" };
const INTER = { fontFamily: "'Inter', system-ui, sans-serif" };

const COLORS = {
  brand: "#0B5394",       // Trust blue
  brandDark: "#073763",
  accent: "#E8A33D",      // Warm gold
  bg: "#FAFAF7",
  panel: "#FFFFFF",
  text: "#1F2937",
  muted: "#5B6470",
  border: "#E5E2DC",
};

const SERVICE_FALLBACK_IMAGES = [
  "https://images.pexels.com/photos/13141770/pexels-photo-13141770.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  "https://images.pexels.com/photos/1669754/pexels-photo-1669754.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  "https://images.pexels.com/photos/12998745/pexels-photo-12998745.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720",
  "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.pexels.com/photos/9301939/pexels-photo-9301939.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  "https://images.pexels.com/photos/8092529/pexels-photo-8092529.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
];

const TRUST_POINTS = [
  { icon: Award, title: "Meisterbetrieb", text: "Geprüfte Qualität nach Handwerksordnung" },
  { icon: CheckCircle2, title: "Festpreis-Garantie", text: "Verbindliches Angebot – keine versteckten Kosten" },
  { icon: Clock, title: "Termintreu", text: "Wir halten zugesagte Termine – schriftlich zugesichert" },
];

const FALLBACK_REVIEWS = [
  { id: "r1", author: "Familie Becker", rating: 5, text: "Absolut sauber gearbeitet, pünktlich und freundlich. Unsere Wohnung sieht aus wie neu!", date: "März 2025" },
  { id: "r2", author: "Markus L.", rating: 5, text: "Fachlich top, ein echter Meister seines Fachs. Die Beratung war ehrlich und das Ergebnis übertrifft unsere Erwartungen.", date: "Februar 2025" },
  { id: "r3", author: "Sabine H.", rating: 5, text: "Vom ersten Kontakt bis zur Endabnahme: höflich, professionell, präzise. Auch der Preis war absolut fair.", date: "Januar 2025" },
];

export default function HomeWerkstatt({ siteInfo, gallery, beforeAfter, reviews, services }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const serviceList = (services && services.length > 0)
    ? services.map((s, i) => ({
        title: s.title,
        text: s.text,
        bullets: s.bullets || [],
        image: s.image_path ? fileUrl(s.image_path) : SERVICE_FALLBACK_IMAGES[i % SERVICE_FALLBACK_IMAGES.length],
      }))
    : [];

  const navLinks = [
    { href: "#ueber-uns", label: "Über uns" },
    { href: "#leistungen", label: "Leistungen" },
    { href: "#referenzen", label: "Referenzen" },
    { href: "#bewertungen", label: "Bewertungen" },
    { href: "#kontakt", label: "Kontakt" },
  ];

  const reviewList = reviews && reviews.length > 0 ? reviews : FALLBACK_REVIEWS;
  const computedAvg = reviewList.length
    ? (reviewList.reduce((a, r) => a + (r.rating || 5), 0) / reviewList.length).toFixed(1)
    : "5.0";
  const ratingText = siteInfo?.google_rating || computedAvg;
  const totalCount = siteInfo?.google_reviews_count > 0 ? siteInfo.google_reviews_count : reviewList.length;
  const [showAllReviews, setShowAllReviews] = useState(false);
  const visibleReviews = showAllReviews ? reviewList : reviewList.slice(0, 6);
  const baFirst = beforeAfter && beforeAfter.length > 0
    ? { beforeSrc: fileUrl(beforeAfter[0].before_path), afterSrc: fileUrl(beforeAfter[0].after_path) }
    : {
        beforeSrc: "https://images.unsplash.com/photo-1503594384566-461fe158e797?auto=format&fit=crop&w=1600&q=80",
        afterSrc: "https://images.pexels.com/photos/13141770/pexels-photo-13141770.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
      };
  const projects = gallery.length > 0
    ? gallery.map((g, i) => ({ id: g.id, title: g.title || `Projekt ${i+1}`, image: fileUrl(g.image_path), description: g.description || "" }))
    : [
        { id: "p1", title: "Modernes Wohnzimmer", image: "https://images.pexels.com/photos/13141770/pexels-photo-13141770.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200", description: "Komplette Neugestaltung in warmen Erdtönen" },
        { id: "p2", title: "Helle Schlafräume", image: "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", description: "Glatte Wände mit Q3-Spachtelung" },
        { id: "p3", title: "Sanierte Fassade", image: "https://images.pexels.com/photos/1669754/pexels-photo-1669754.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200", description: "Fassadenanstrich + WDVS" },
      ];

  const handleContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Vielen Dank! Wir melden uns in Kürze.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Senden.");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="home-werkstatt" style={{ ...INTER, backgroundColor: COLORS.bg, color: COLORS.text }} className="min-h-screen">
      {/* TOP CONTACT BAR */}
      <div style={{ backgroundColor: COLORS.brandDark, color: "#FFFFFF" }} className="hidden md:block text-sm">
        <div className="max-w-[1280px] mx-auto px-6 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href={`tel:${siteInfo?.phone}`} className="flex items-center gap-2 hover:text-[#E8A33D] transition-colors"><Phone className="w-3.5 h-3.5" />{siteInfo?.phone}</a>
            <a href={`mailto:${siteInfo?.email}`} className="flex items-center gap-2 hover:text-[#E8A33D] transition-colors"><Mail className="w-3.5 h-3.5" />{siteInfo?.email}</a>
          </div>
          <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" />{siteInfo?.business_hours}</div>
        </div>
      </div>

      {/* MAIN HEADER */}
      <header className="sticky top-0 z-40 bg-white border-b" style={{ borderColor: COLORS.border }}>
        <div className="max-w-[1280px] mx-auto px-6 py-5 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-4" data-testid="werkstatt-navbar-logo">
            {siteInfo?.logo_path ? (
              <img src={fileUrl(siteInfo.logo_path)} alt="Logo" className="h-14 w-auto object-contain" />
            ) : (
              <div className="flex items-center gap-3">
                <span className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold" style={{ backgroundColor: COLORS.brand, ...BITTER }}>
                  {(siteInfo?.company_name || "SR").split(" ").map(w => w[0]).slice(-2).join("")}
                </span>
                <div>
                  <p style={BITTER} className="text-xl leading-tight" >{siteInfo?.company_name}</p>
                  <p className="text-xs" style={{ color: COLORS.muted }}>{siteInfo?.tagline}</p>
                </div>
              </div>
            )}
          </a>
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} className="text-sm font-medium hover:text-[#0B5394] transition-colors" style={{ color: COLORS.text }}>{l.label}</a>
            ))}
            <a href="#kontakt" className="px-5 py-2.5 text-white text-sm rounded font-medium hover:opacity-90 transition-opacity" style={{ backgroundColor: COLORS.brand }}>
              Kostenloses Angebot
            </a>
          </nav>
          <button onClick={() => setOpen(!open)} className="lg:hidden" style={{ color: COLORS.text }}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t" style={{ borderColor: COLORS.border }}>
            <nav className="px-6 py-4 flex flex-col gap-3">
              {navLinks.map(l => <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="py-2">{l.label}</a>)}
            </nav>
          </div>
        )}
      </header>

      {/* HERO — Split intro + portrait */}
      <section id="top" className="py-16 lg:py-24" style={{ backgroundColor: COLORS.bg }}>
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="lg:col-span-7">
            <p className="inline-block px-3 py-1.5 rounded-full text-xs font-semibold mb-6 uppercase tracking-wide" style={{ backgroundColor: "#E8F0F9", color: COLORS.brand }}>
              ★ Meisterbetrieb · seit {new Date().getFullYear() - (siteInfo?.years_experience || 25)}
            </p>
            <h1 style={BITTER} className="text-5xl lg:text-6xl leading-[1.1] mb-6" data-testid="werkstatt-hero-headline">
              Schöne Wände. <br />
              <span style={{ color: COLORS.brand }}>Ehrliches Handwerk.</span>
            </h1>
            <p className="text-lg leading-relaxed mb-8" style={{ color: COLORS.muted }}>
              {siteInfo?.description}
            </p>
            <div className="flex flex-wrap gap-4 mb-10">
              <a href="#kontakt" className="px-7 py-3.5 text-white rounded font-semibold flex items-center gap-2 hover:opacity-90 transition" style={{ backgroundColor: COLORS.brand }}>
                Kostenloses Angebot <ChevronRight className="w-4 h-4" />
              </a>
              <a href="tel:${siteInfo?.phone}" className="px-7 py-3.5 rounded font-semibold border-2 flex items-center gap-2 hover:bg-white transition" style={{ borderColor: COLORS.brand, color: COLORS.brand }}>
                <Phone className="w-4 h-4" /> {siteInfo?.phone}
              </a>
            </div>
            <div className="flex flex-wrap gap-6 items-center text-sm" style={{ color: COLORS.muted }}>
              <div className="flex items-center gap-1.5"><div className="flex">{[1,2,3,4,5].map(i => <Star key={i} className="w-3.5 h-3.5 fill-[#E8A33D] text-[#E8A33D]" />)}</div> {ratingText} Google-Bewertung</div>
              <span>·</span>
              <span>{siteInfo?.projects_completed || 480}+ Projekte realisiert</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.15 }} className="lg:col-span-5 relative">
            <div className="aspect-[4/5] overflow-hidden rounded-lg shadow-xl">
              <img src={siteInfo?.hero_image_path ? fileUrl(siteInfo.hero_image_path) : "https://images.pexels.com/photos/1669754/pexels-photo-1669754.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"} alt="Malermeister bei der Arbeit" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-lg shadow-lg p-5 max-w-[220px] hidden md:block">
              <Award className="w-7 h-7 mb-3" style={{ color: COLORS.accent }} />
              <p style={BITTER} className="text-lg leading-tight mb-1">100% Faires Handwerk</p>
              <p className="text-xs" style={{ color: COLORS.muted }}>Festpreis · Termintreue · Meisterqualität</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="py-10 bg-white border-y" style={{ borderColor: COLORS.border }}>
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          {TRUST_POINTS.map((t, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#E8F0F9", color: COLORS.brand }}>
                <t.icon className="w-5 h-5" />
              </div>
              <div>
                <p style={BITTER} className="text-lg mb-1">{t.title}</p>
                <p className="text-sm" style={{ color: COLORS.muted }}>{t.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ÜBER UNS */}
      <section id="ueber-uns" className="py-20 lg:py-28">
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: COLORS.accent }}>Über uns</p>
            <h2 style={BITTER} className="text-4xl lg:text-5xl leading-tight">Ein Familienbetrieb. <br /><span style={{ color: COLORS.brand }}>Drei Generationen Handwerk.</span></h2>
          </div>
          <div className="lg:col-span-7 space-y-4 text-base lg:text-lg leading-relaxed" style={{ color: COLORS.muted }}>
            <p>{siteInfo?.description}</p>
            <p>Unser Anspruch: Jeder Pinselstrich soll sitzen. Jede Wand soll Charakter haben. Jeder Kunde soll am Ende sagen: &bdquo;Das hätten wir uns nicht schöner vorstellen können.&ldquo;</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4">
              {["Geprüfter Meisterbetrieb", "Eigene Mitarbeiter (keine Subunternehmer)", "Beratung vor Ort kostenlos", "Versichert & garantiert", "Verbindliche Festpreise", "Termintreu zugesagt"].map((p) => (
                <li key={p} className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: COLORS.brand }} /><span style={{ color: COLORS.text }}>{p}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* LEISTUNGEN — Alternating image/text rows */}
      <section id="leistungen" className="py-20 lg:py-28 bg-white">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: COLORS.accent }}>Unsere Leistungen</p>
            <h2 style={BITTER} className="text-4xl lg:text-5xl mb-4">Alles rund um Ihre Hausgestaltung</h2>
            <p className="text-lg" style={{ color: COLORS.muted }}>Ein Ansprechpartner. Saubere Ausführung. Faire Preise.</p>
          </div>

          <div className="space-y-16 lg:space-y-20">
            {serviceList.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7 }}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
                data-testid={`werkstatt-service-${i}`}
              >
                <div className="lg:col-span-6 aspect-[16/10] overflow-hidden rounded-lg shadow-md">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="lg:col-span-6">
                  <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: COLORS.accent }}>{String(i + 1).padStart(2, "0")} · Leistung</p>
                  <h3 style={BITTER} className="text-3xl lg:text-4xl mb-4 leading-tight">{s.title}</h3>
                  <p className="text-lg leading-relaxed mb-6" style={{ color: COLORS.muted }}>{s.text}</p>
                  <ul className="space-y-2 mb-6">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: COLORS.brand }}></div><span>{b}</span></li>
                    ))}
                  </ul>
                  <a href="#kontakt" className="inline-flex items-center gap-2 font-semibold hover:gap-3 transition-all" style={{ color: COLORS.brand }}>
                    Angebot anfordern <ChevronRight className="w-4 h-4" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* REFERENZEN / NEWS GRID */}
      <section id="referenzen" className="py-20 lg:py-28" style={{ backgroundColor: "#F1F4F8" }}>
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: COLORS.accent }}>Aktuelle Referenzen</p>
              <h2 style={BITTER} className="text-4xl lg:text-5xl">Unsere letzten Projekte</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {projects.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow group"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: COLORS.accent }}>Projekt</p>
                  <h3 style={BITTER} className="text-xl mb-2">{p.title}</h3>
                  {p.description && <p className="text-sm" style={{ color: COLORS.muted }}>{p.description}</p>}
                </div>
              </motion.article>
            ))}
          </div>

          {/* VORHER/NACHHER */}
          <div className="bg-white rounded-lg overflow-hidden shadow-md p-6 lg:p-10">
            <div className="text-center mb-8">
              <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: COLORS.accent }}>Vorher / Nachher</p>
              <h3 style={BITTER} className="text-3xl">Sehen Sie selbst die Verwandlung</h3>
            </div>
            <BeforeAfterSlider beforeSrc={baFirst.beforeSrc} afterSrc={baFirst.afterSrc} />
          </div>
        </div>
      </section>

      {/* BEWERTUNGEN */}
      <section id="bewertungen" className="py-20 lg:py-28 bg-white">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: COLORS.accent }}>Was Kunden sagen</p>
            <h2 style={BITTER} className="text-4xl lg:text-5xl mb-4">Echte Bewertungen. Echte Kunden.</h2>
            <div className="flex items-center justify-center gap-3">
              <div className="flex">{[1,2,3,4,5].map(i => <Star key={i} className="w-6 h-6 fill-[#E8A33D] text-[#E8A33D]" />)}</div>
              <span style={BITTER} className="text-2xl">{ratingText} / 5</span>
              <span className="text-sm" style={{ color: COLORS.muted }}>aus {totalCount}+ Bewertungen</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
            {visibleReviews.map((r, i) => (
              <motion.article
                key={r.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border rounded-lg p-7 flex flex-col"
                style={{ borderColor: COLORS.border, backgroundColor: COLORS.bg }}
              >
                <div className="flex mb-4">{Array.from({length: r.rating || 5}).map((_, idx) => <Star key={idx} className="w-4 h-4 fill-[#E8A33D] text-[#E8A33D]" />)}</div>
                <p className="leading-relaxed mb-6 flex-1" style={{ color: COLORS.text }}>
                  <ReviewText
                    text={r.text}
                    threshold={260}
                    buttonClassName="text-sm font-semibold"
                    showMoreLabel="Weiterlesen"
                    showLessLabel="Weniger anzeigen"
                  />
                </p>
                <div className="pt-4 border-t mt-auto" style={{ borderColor: COLORS.border }}>
                  <p style={BITTER} className="text-base">{r.author}</p>
                  {r.date && <p className="text-xs mt-1" style={{ color: COLORS.muted }}>{r.date}</p>}
                </div>
              </motion.article>
            ))}
          </div>
          {reviewList.length > 6 && (
            <div className="text-center mt-10">
              <button
                type="button"
                onClick={() => setShowAllReviews(!showAllReviews)}
                data-testid="reviews-toggle"
                className="px-7 py-3.5 text-white rounded font-semibold hover:opacity-90 transition"
                style={{ backgroundColor: COLORS.brand }}
              >
                {showAllReviews ? "Weniger anzeigen" : `Alle ${reviewList.length} Bewertungen anzeigen`}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* KONTAKT */}
      <section id="kontakt" className="py-20 lg:py-28" style={{ backgroundColor: COLORS.brandDark, color: "#FFFFFF" }}>
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: COLORS.accent }}>Kontakt</p>
            <h2 style={BITTER} className="text-4xl lg:text-5xl mb-6 leading-tight">Sprechen wir über Ihr Projekt</h2>
            <p className="text-lg text-white/80 leading-relaxed mb-10">Rufen Sie uns an oder schreiben Sie uns. Wir erstellen Ihnen gerne ein kostenloses und unverbindliches Angebot — und kommen für die Besichtigung gerne zu Ihnen vor Ort.</p>
            <ul className="space-y-5">
              <li className="flex gap-4 items-start"><Phone className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: COLORS.accent }} /><div><p className="text-xs uppercase tracking-wider text-white/60 mb-1">Telefon</p><a href={`tel:${siteInfo?.phone}`} className="text-lg hover:text-[#E8A33D] transition">{siteInfo?.phone}</a></div></li>
              <li className="flex gap-4 items-start"><Mail className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: COLORS.accent }} /><div><p className="text-xs uppercase tracking-wider text-white/60 mb-1">E-Mail</p><a href={`mailto:${siteInfo?.email}`} className="text-lg hover:text-[#E8A33D] transition">{siteInfo?.email}</a></div></li>
              <li className="flex gap-4 items-start"><MapPin className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: COLORS.accent }} /><div><p className="text-xs uppercase tracking-wider text-white/60 mb-1">Adresse</p><p className="text-lg">{siteInfo?.address}</p></div></li>
              <li className="flex gap-4 items-start"><Clock className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: COLORS.accent }} /><div><p className="text-xs uppercase tracking-wider text-white/60 mb-1">Öffnungszeiten</p><p className="text-lg">{siteInfo?.business_hours}</p></div></li>
            </ul>
          </div>

          <form onSubmit={handleContact} className="lg:col-span-7 bg-white text-stone-900 rounded-lg p-8 lg:p-10" data-testid="werkstatt-contact-form">
            <h3 style={BITTER} className="text-2xl mb-6">Kostenloses Angebot anfordern</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div><Label className="mb-1.5 block text-sm font-medium" style={{color: COLORS.text}}>Name *</Label><Input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-white" /></div>
              <div><Label className="mb-1.5 block text-sm font-medium" style={{color: COLORS.text}}>E-Mail *</Label><Input required type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-white" /></div>
              <div><Label className="mb-1.5 block text-sm font-medium" style={{color: COLORS.text}}>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-white" /></div>
              <div><Label className="mb-1.5 block text-sm font-medium" style={{color: COLORS.text}}>Betreff</Label><Input value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} className="bg-white" /></div>
            </div>
            <div className="mb-6"><Label className="mb-1.5 block text-sm font-medium" style={{color: COLORS.text}}>Ihre Nachricht *</Label><Textarea required rows={6} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} className="bg-white" /></div>
            <label className="mb-6 flex items-start gap-3 text-sm cursor-pointer" style={{ color: COLORS.muted }} data-testid="werkstatt-privacy-consent">
              <input type="checkbox" required data-testid="werkstatt-privacy-checkbox" className="mt-1 h-4 w-4" />
              <span>Ich habe die <Link to="/datenschutz" className="underline hover:opacity-80" style={{ color: COLORS.brand }}>Datenschutzerklärung</Link> gelesen und stimme der Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage zu. *</span>
            </label>
            <button type="submit" disabled={sending} className="w-full sm:w-auto px-8 py-3.5 text-white rounded font-semibold hover:opacity-90 transition" style={{ backgroundColor: COLORS.brand }}>
              {sending ? "Wird gesendet..." : "Anfrage senden"}
            </button>
            <p className="text-xs mt-3" style={{ color: COLORS.muted }}>Wir antworten in der Regel innerhalb von 24 Stunden.</p>
          </form>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 text-white" style={{ backgroundColor: "#06223F" }}>
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="flex items-center gap-3">
            {siteInfo?.logo_path && <img src={fileUrl(siteInfo.logo_path)} alt="" className="h-12 w-auto object-contain bg-white rounded p-1" />}
            <div>
              <p style={BITTER} className="text-lg">{siteInfo?.company_name}</p>
              <p className="text-xs text-white/60">{siteInfo?.tagline}</p>
            </div>
          </div>
          <p className="text-xs text-white/60 text-center">© {new Date().getFullYear()} {siteInfo?.company_name}. Alle Rechte vorbehalten.</p>
          <div className="flex justify-end gap-4 text-xs text-white/60">
            <Link to="/impressum" data-testid="footer-impressum-link" className="hover:text-[#E8A33D] transition-colors">Impressum</Link>
            <Link to="/datenschutz" data-testid="footer-datenschutz-link" className="hover:text-[#E8A33D] transition-colors">Datenschutz</Link>
            <Link to="/admin/login" className="hover:text-[#E8A33D] transition-colors">Admin</Link>
          </div>
        </div>
        <div className="max-w-[1280px] mx-auto px-6 mt-6 pt-6 border-t border-white/10 text-center">
          <a
            href="https://www.kauzwerk.de"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-credit"
            className="text-xs text-white/50 hover:text-[#E8A33D] transition-colors"
          >
            made by <span style={BITTER} className="text-white/80">KauzWerk</span> · www.kauzwerk.de
          </a>
        </div>
      </footer>
    </div>
  );
}
